import { motion } from "framer-motion";
import { Gauge } from "lucide-react";

const WEIGHTS = [
  { label: "Audience Fit", weight: 30, score: 96 },
  { label: "Niche Relevance", weight: 20, score: 94 },
  { label: "Engagement", weight: 15, score: 88 },
  { label: "Activity", weight: 10, score: 90 },
  { label: "Collaboration Potential", weight: 10, score: 91 },
  { label: "Audience Size", weight: 5, score: 84 },
  { label: "Contactability", weight: 5, score: 89 },
  { label: "Commercial Potential", weight: 5, score: 92 },
];

export default function Score() {
  return (
    <section id="score" data-testid="score-section" className="border-t border-white/10 bg-[#0A0A0A]">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-2 lg:items-center lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            The Partnership Opportunity Score
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Every prospect, ranked 0&ndash;100. Every score, explained.
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
            No black boxes. Eight transparent weightings decide who deserves your time — with
            audience fit counting six times more than audience size. Because a room of 5,000
            buyers beats a stadium of strangers.
          </p>
          <blockquote className="mt-8 border-l-2 border-[#D4AF37] pl-6 font-serif text-lg italic leading-relaxed text-zinc-300">
            &ldquo;Highly relevant DELF audience, active community, frequent B2 exam discussions,
            administrator open to educational presentations, strong product-audience fit.&rdquo;
          </blockquote>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
            — Scoring Agent, sample verdict
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="score-widget"
          className="border border-[#27272A] bg-[#050505]"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-8 py-6">
            <div className="flex items-center gap-3">
              <Gauge className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
                Prospect Evaluation
              </span>
            </div>
            <span className="rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
              High Priority
            </span>
          </div>
          <div className="border-b border-white/10 px-8 py-8">
            <p className="font-serif text-7xl font-semibold leading-none">
              92<span className="text-3xl text-zinc-500">/100</span>
            </p>
            <p className="mt-2 text-sm text-zinc-500">Facebook Group — DELF B2 Candidates</p>
          </div>
          <ul className="divide-y divide-white/5">
            {WEIGHTS.map((w) => (
              <li key={w.label} className="flex items-center gap-4 px-8 py-3.5">
                <span className="w-44 shrink-0 text-xs font-medium text-zinc-300">{w.label}</span>
                <span className="w-10 shrink-0 text-[10px] uppercase tracking-wider text-zinc-500">
                  {w.weight}%
                </span>
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${w.score}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="h-full rounded-full bg-[#D4AF37]"
                  />
                </div>
                <span className="w-8 text-right text-xs font-semibold text-zinc-300">{w.score}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
